import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { useT } from '../../../i18n';
import { ChonKhoang } from '../chung/components/ChonKhoang';
import { ThanhTab } from '../chung/components/ThanhTab';
import { KHOANG_MAC_DINH, thamSoKhoang, type KhoangAi } from '../chung/nghiep-vu/khoang';
import {
  LOC_AI_MAC_DINH,
  coLocAi,
  nhanKhoaPhanRa,
  truongCuaTab,
  tuyChonLocAi,
  type LocAi,
  type TabSuDungAi,
} from '../chung/nghiep-vu/loc-ai';
import type { NhomPhanRa } from '../chung/nghiep-vu/types-thong-ke';
import { BangLuong } from './components/BangLuong';
import { BangLuotGoi } from './components/BangLuotGoi';
import { BangTrace } from './components/BangTrace';
import { BoLocAi } from './components/BoLocAi';
import { TongQuanAi } from './components/TongQuanAi';
import { useBoLocAi, useDangTaiNktv } from './hooks';
import { useNguonVet } from './nguon';
import { khoangBiKep } from './nghiep-vu/vet';

/**
 * Nhật ký sử dụng AI — mục 7.11 và 8.2, cộng phần dòng chảy token. Port `views/SuDungAiView.tsx` native.
 *
 * Không còn là màn riêng: một thẻ của Cài đặt bảo mật ở cổng tenant và của Nhật ký hệ thống ở cổng nền tảng. Nơi nhúng
 * bọc khối này trong `NguonVetTenant` hoặc `NguonVetNenTang` (`nguon.tsx`); `dauThanh` là chỗ cổng nền tảng đặt ô
 * chọn tenant, cùng hàng với bộ chọn khoảng.
 *
 * ── VÌ SAO BỐN TAB CHỨ KHÔNG BỐN MỤC THANH BÊN ──────────────────────────
 * Dòng chảy token (chuỗi xử lý, lượt gọi, luồng theo đối tượng) là ba cách nhìn CÙNG một tập dữ liệu mà tab "Tổng quan"
 * tổng hợp, và người ta đi từ con số tổng xuống chuỗi cụ thể rồi xuống một lượt gọi. Tách ra thành mục thanh bên riêng
 * là bắt họ mang theo khoảng thời gian bằng tay giữa các trang.
 *
 * ── KHOẢNG THỜI GIAN VÀ BỘ LỌC NẰM Ở ĐÂY, KHÔNG Ở TỪNG TAB ──────────────
 * Đổi tab mà số đổi theo vì mỗi tab có khoảng riêng là cách người đọc kết luận sai. `neo` quy "24 giờ qua" thành một
 * mốc tuyệt đối và chỉ đổi khi người dùng làm gì đó — lấy `Date.now()` lúc render thì mỗi render một khoá truy vấn mới.
 * Mỗi tab chỉ HIỆN ô lọc mà endpoint của nó áp (`TRUONG_THEO_TAB`), nhưng giữ chung một trạng thái lọc.
 *
 * Khác native: tenant chỉ thấy token Fox, chia đầu vào và đầu ra (ADR-0123 mục 1, 5). Token thật, giá vốn và lối sang giá
 * vốn model chỉ có ở cổng nền tảng. Liên kết Langfuse đứng sau quyền kỹ thuật và chỉ hiện khi máy chủ trả URL.
 */
export function KhoiSuDungAi({ dauThanh }: { dauThanh?: ReactNode }) {
  const { t, locale } = useT();
  const [khoang, setKhoang] = useState<KhoangAi>(KHOANG_MAC_DINH);
  const [neo, setNeo] = useState<number>(() => Date.now());
  const [loc, setLoc] = useState<LocAi>(LOC_AI_MAC_DINH);

  const datKhoang = useCallback((k: KhoangAi) => {
    setKhoang(k);
    setNeo(Date.now());
  }, []);
  const lamMoi = useCallback(() => setNeo(Date.now()), []);

  const { tu, den } = useMemo(() => thamSoKhoang(khoang, neo), [khoang, neo]);
  const biKep = khoangBiKep(khoang, tu);
  const boLoc = useBoLocAi(tu, den);
  const { tenDonVi: danhBaDonVi, nenTang, lienKetBangGia } = useNguonVet();
  const xemGiaVon = lienKetBangGia !== null;
  const dangTai = useDangTaiNktv();
  const chon = useMemo(() => tuyChonLocAi(boLoc.data, danhBaDonVi, locale), [boLoc.data, danhBaDonVi, locale]);
  const nhanKhoa = useCallback((nhom: NhomPhanRa, khoa: string) => nhanKhoaPhanRa(chon, nhom, khoa), [chon]);

  const thanhLoc = (k: TabSuDungAi) => (
    <BoLocAi loc={loc} onDoi={setLoc} chon={chon} truong={truongCuaTab(k, nenTang, xemGiaVon)} loiGoiY={boLoc.isError} />
  );

  return (
    <div className="space-y-4">
      {!nenTang && <p className="text-xs leading-relaxed text-content-muted">{t('nktvSuDungAi.chuThichTenant')}</p>}

      {biKep && (
        <p className="rounded-lg bg-warning-50 px-4 py-2.5 text-xs leading-relaxed text-warning-700" role="status">
          {t('nktv.khoang.biKep')}
        </p>
      )}

      <ThanhTab
        nhan={t('nktvSuDungAi.tieuDe')}
        macDinh="tong-quan"
        phai={
          <>
            {dauThanh}
            <ChonKhoang khoang={khoang} onDoi={datKhoang} onLamMoi={lamMoi} dangTai={dangTai} />
          </>
        }
        items={[
          {
            key: 'tong-quan',
            label: t('nktvSuDungAi.tab.tongQuan'),
            children: (
              <div className="space-y-4">
                {thanhLoc('tong-quan')}
                <TongQuanAi
                  tu={tu}
                  den={den}
                  loc={loc}
                  coLoc={coLocAi(loc, truongCuaTab('tong-quan', nenTang, xemGiaVon))}
                  nhanKhoa={nhanKhoa}
                />
              </div>
            ),
          },
          {
            key: 'trace',
            label: t('nktvSuDungAi.tab.trace'),
            children: (
              <div className="space-y-4">
                {thanhLoc('trace')}
                <BangTrace tu={tu} den={den} loc={loc} />
              </div>
            ),
          },
          {
            key: 'luot-goi',
            label: t('nktvSuDungAi.tab.luotGoi'),
            children: (
              <div className="space-y-4">
                {thanhLoc('luot-goi')}
                <BangLuotGoi tu={tu} den={den} loc={loc} />
              </div>
            ),
          },
          {
            key: 'luong',
            label: t('nktvSuDungAi.tab.luong'),
            children: <BangLuong tu={tu} den={den} />,
          },
        ]}
      />
    </div>
  );
}
